import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  FormLayout,
  TextField,
  Select,
  Stack,
  TextContainer,
  Banner,
  Loading,
  Modal,
  Form,
  Text,
  List,
  ButtonGroup,
  Tabs,
  Icon,
  Badge,
  DataTable,
  EmptyState,
  Link
} from '@shopify/polaris';
import { DuplicateMinor } from '@shopify/polaris-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { saveOpenAISettings, getOpenAISettings, fetchOpenAIModels } from '../services/openaiService';
import { useShop } from '../contexts/ShopContext';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function Settings() {
  const { user, logout, resetPassword, updatePassword, updateFullName } = useAuth();
  const { currentShop } = useShop();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // User Profile State
  const [userData, setUserData] = useState(null);
  const [showFullNameModal, setShowFullNameModal] = useState(false);
  const [newFullName, setNewFullName] = useState('');

  // Password Change State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // OpenAI Settings
  const [openaiSettings, setOpenaiSettings] = useState({
    apiKey: '',
    model: 'gpt-4o-mini',
    temperature: 0.7,
    maxTokens: 2000
  });
  const [selectedModel, setSelectedModel] = useState('');
  const [currentMaxTokens, setCurrentMaxTokens] = useState(0);
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);

  // Referral System State
  const [selectedTab, setSelectedTab] = useState(0);
  const [referralInfo, setReferralInfo] = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [isReferralPartner, setIsReferralPartner] = useState(false);
  const [paypalEmail, setPaypalEmail] = useState('');
  const [showPaypalModal, setShowPaypalModal] = useState(false);
  const [showReferralInfoModal, setShowReferralInfoModal] = useState(false);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [pendingCommissions, setPendingCommissions] = useState(0);
  const [syncingReferrals, setSyncingReferrals] = useState(false);

  // Model token limits
  const modelTokenLimits = {
    'gpt-4': 8192,
    'gpt-4-32k': 32768,
    'gpt-4-1106-preview': 128000,
    'gpt-4o': 128000,
    'gpt-4o-mini': 128000,
    'gpt-3.5-turbo': 4096,
    'gpt-3.5-turbo-16k': 16385,
    'gpt-3.5-turbo-1106': 16385
  };

  // Load user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!user?.uid) return;
        
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
          if (userDoc.data().fullName) {
            setNewFullName(userDoc.data().fullName);
          }
          
          // Check if user is a referral partner
          if (userDoc.data().isReferralPartner) {
            setIsReferralPartner(true);
            setReferralCode(userDoc.data().partnerReferralCode || '');
            fetchReferralInfo();
          } else if (userDoc.data().referralCode) {
            // User has a referral code but is not a partner (they were referred by someone)
            setReferralCode(userDoc.data().referralCode || '');
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    
    fetchUserData();
  }, [user]);

  // Load OpenAI settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        if (!user?.uid) {
          console.log('No user ID available, skipping settings load');
          return;
        }

        console.log('Loading OpenAI settings for user:', user.uid);
        const settings = await getOpenAISettings(user.uid);
        
        if (settings) {
          console.log('Loaded OpenAI settings:', settings);
          setOpenaiSettings(settings);
          // Set the selected model and its token limit
          if (settings.model) {
            setSelectedModel(settings.model);
            setCurrentMaxTokens(modelTokenLimits[settings.model] || 4096);
          }
          
          // If we have an API key, fetch available models
          if (settings.apiKey) {
            setLoadingModels(true);
            try {
              const availableModels = await fetchOpenAIModels(settings.apiKey);
              setModels(availableModels);
            } catch (error) {
              console.error('Error fetching models:', error);
              // Don't show error to user as this is just initial load
            } finally {
              setLoadingModels(false);
            }
          }
        } else {
          // Initialize default settings for new users
          console.log('No settings found, initializing defaults');
          const defaultSettings = {
            apiKey: '',
            model: 'gpt-4o-mini',
            temperature: 0.7,
            maxTokens: 2000
          };
          await saveOpenAISettings(user.uid, defaultSettings);
          setOpenaiSettings(defaultSettings);
          setSelectedModel(defaultSettings.model);
          setCurrentMaxTokens(modelTokenLimits[defaultSettings.model] || 4096);
        }
      } catch (error) {
        console.error('Error loading OpenAI settings:', error);
        setError('Failed to load OpenAI settings. Please try again.');
      }
    };

    loadSettings();
  }, [user]);

  // Save API Key
  const handleSaveApiKey = async () => {
    try {
      setLoading(true);
      setLoadingModels(true);
      setError('');
      
      if (!user?.uid) {
        throw new Error('Must be logged in to save settings');
      }

      // First save the API key
      console.log('Saving OpenAI settings for user:', user.uid);
      await saveOpenAISettings(user.uid, openaiSettings);

      // Then fetch available models
      console.log('Fetching OpenAI models with new API key');
      const availableModels = await fetchOpenAIModels(openaiSettings.apiKey);
      setModels(availableModels);
      
      setSuccess('API key saved and models loaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error saving OpenAI settings:', error);
      setError(error.message || 'Failed to save settings or fetch models. Please check your API key.');
    } finally {
      setLoading(false);
      setLoadingModels(false);
    }
  };

  // Save selected model
  const handleSaveModel = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Saving model:', selectedModel);
      
      const maxTokens = modelTokenLimits[selectedModel] || 4096;
      setCurrentMaxTokens(maxTokens);
      console.log('Using max tokens:', maxTokens);

      const settings = {
        ...openaiSettings,
        model: selectedModel,
        maxTokens: maxTokens
      };

      console.log('Saving settings:', settings);

      await saveOpenAISettings(user.uid, settings);
      setOpenaiSettings(settings);
      
      setSuccess('Model selection and configuration saved successfully');
    } catch (error) {
      console.error('Detailed error saving model selection:', error);
      setError(error.message || 'Failed to save model selection');
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/app/login');
    } catch (error) {
      console.error('Logout error:', error);
      setError('Failed to log out');
    }
  };

  // Handle password reset
  const handlePasswordReset = async () => {
    try {
      if (!user?.email) {
        throw new Error('No email address available');
      }

      setLoading(true);
      setError('');
      await resetPassword(user.email);
      setSuccess('Password reset email sent. Please check your inbox.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handleChangePassword = async () => {
    try {
      setError('');
      setLoading(true);

      // Validation
      if (newPassword !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters');
      }

      await updatePassword(currentPassword, newPassword);
      
      // Clear form and show success
      setSuccess('Password updated successfully');
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Change password error:', error);
      setError(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Handle full name update
  const handleUpdateFullName = async () => {
    try {
      setError('');
      setLoading(true);

      if (!newFullName.trim()) {
        throw new Error('Full name cannot be empty');
      }

      await updateFullName(newFullName);
      
      // Update local state
      if (userData) {
        setUserData({
          ...userData,
          fullName: newFullName
        });
      }
      
      // Clear form and show success
      setSuccess('Full name updated successfully');
      setShowFullNameModal(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Update full name error:', error);
      setError(error.message || 'Failed to update full name');
    } finally {
      setLoading(false);
    }
  };

  // Update PayPal email for referral partner
  const handleUpdatePaypalEmail = async () => {
    try {
      setLoading(true);
      
      if (!user?.uid) {
        throw new Error('You must be logged in to update your PayPal email');
      }
      
      // Fetch the latest referral partner information
      const referralSystemRef = doc(db, 'referralSystem', user.uid);
      const referralDoc = await getDoc(referralSystemRef);
      
      if (!referralDoc.exists()) {
        throw new Error('You are not registered as a referral partner');
      }
      
      // Update the referral partner document with the PayPal email
      await updateDoc(referralSystemRef, {
        paypalEmail: paypalEmail,
        updatedAt: new Date().toISOString()
      });
      
      // Update local state
      setReferralInfo(prev => ({
        ...prev,
        paypalEmail: paypalEmail
      }));
      
      setSuccess('PayPal email updated successfully');
      setShowPaypalModal(false);
      setTimeout(() => setSuccess(''), 3000);
      
      // Refresh referral info
      fetchReferralInfo();
    } catch (error) {
      console.error('Error updating PayPal email:', error);
      setError(error.message || 'Failed to update PayPal email');
    } finally {
      setLoading(false);
    }
  };

  // Fetch referral information and referred users
  const fetchReferralInfo = async () => {
    try {
      if (!user?.uid) return;
      
      setLoadingReferrals(true);
      
      // First check if the user is a referral partner in the users collection
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) return;
      
      const userData = userDoc.data();
      const isUserAdmin = userData.role === 'admin';
      
      if (!userData.isReferralPartner) {
        setIsReferralPartner(false);
        setLoadingReferrals(false);
        return;
      }
      
      // User is a referral partner, prioritize the centralized referral system
      let partnerData = {};
      let referredUsersData = [];
      
      try {
        const referralSystemDoc = await getDoc(doc(db, 'referralSystem', user.uid));
        
        // If we have data in the centralized system, use it
        if (referralSystemDoc.exists()) {
          console.log('Found referral system data for partner');
          const referralSystemData = referralSystemDoc.data();
          
          partnerData = {
            id: user.uid,
            code: referralSystemData.code || userData.partnerReferralCode,
            commission: referralSystemData.commission || 10,
            active: referralSystemData.isActive !== false,
            referralCount: referralSystemData.referralStats?.total || 0,
            paypalEmail: referralSystemData.paypalEmail || userData.paypalEmail,
            createdAt: referralSystemData.createdAt,
            updatedAt: referralSystemData.updatedAt,
            partnerName: referralSystemData.partnerName || userData.fullName || '',
            partnerEmail: referralSystemData.partnerEmail || userData.email || ''
          };
          
          // Get referred users from the centralized system
          if (referralSystemData.referredUsers && referralSystemData.referredUsers.length > 0) {
            console.log(`Found ${referralSystemData.referredUsers.length} referred users in the centralized system`);
            referredUsersData = referralSystemData.referredUsers.map(ru => ({
              id: ru.userId,
              email: ru.email,
              fullName: ru.fullName,
              createdAt: ru.registeredAt,
              activatedAt: ru.activatedAt,
              subscription: ru.subscription || null
            }));
          }
        } else {
          // If no data in centralized system, check users collection for referral code
          console.log('No data in centralized system, checking user document');
          
          partnerData = {
            id: user.uid,
            code: userData.partnerReferralCode || '',
            commission: userData.referralCommission || 10,
            active: true,
            referralCount: 0,
            paypalEmail: userData.paypalEmail || '',
            createdAt: userData.createdAt,
            updatedAt: userData.updatedAt,
            partnerName: userData.fullName || '',
            partnerEmail: userData.email || ''
          };
          
          // Create the referral system document if it doesn't exist
          if (partnerData.code) {
            console.log('Creating referral system document for partner');
            try {
              await setDoc(doc(db, 'referralSystem', user.uid), {
                partnerId: user.uid,
                partnerName: userData.fullName || '',
                partnerEmail: userData.email || '',
                code: partnerData.code,
                commission: partnerData.commission,
                isActive: true,
                referredUsers: [],
                referralStats: { total: 0, active: 0 },
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              });
            } catch (error) {
              console.error('Error creating referral system document:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching from referral system:', error);
        // Continue with user data only
        partnerData = {
          id: user.uid,
          code: userData.partnerReferralCode || '',
          commission: userData.referralCommission || 10,
          active: true,
          referralCount: 0,
          paypalEmail: userData.paypalEmail || '',
          createdAt: userData.createdAt,
          updatedAt: userData.updatedAt,
          partnerName: userData.fullName || '',
          partnerEmail: userData.email || ''
        };
      }
      
      // If we still don't have referred users, check users collection
      if (referredUsersData.length === 0 && partnerData.code) {
        console.log('Checking users collection for referred users');
        try {
          const usersRef = collection(db, 'users');
          const referredUsersQuery = query(usersRef, where('referrerId', '==', user.uid));
          const usersSnapshot = await getDocs(referredUsersQuery);
          
          if (!usersSnapshot.empty) {
            console.log(`Found ${usersSnapshot.docs.length} referred users in users collection`);
            const usersData = usersSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            // Format the user data
            referredUsersData = usersData.map(userData => ({
              id: userData.id,
              email: userData.email || '',
              fullName: userData.fullName || '',
              createdAt: userData.createdAt || '',
              activatedAt: userData.updatedAt || '',
              subscription: userData.subscription || null
            }));
            
            // Update the referral system document with these users
            if (referredUsersData.length > 0) {
              console.log('Updating referral system with found users');
              try {
                const referralSystemRef = doc(db, 'referralSystem', user.uid);
                await updateDoc(referralSystemRef, {
                  referredUsers: referredUsersData.map(ru => ({
                    userId: ru.id,
                    email: ru.email,
                    fullName: ru.fullName,
                    registeredAt: ru.createdAt,
                    activatedAt: ru.activatedAt,
                    subscription: ru.subscription
                  })),
                  referralStats: { 
                    total: referredUsersData.length, 
                    active: referredUsersData.length 
                  },
                  updatedAt: new Date().toISOString()
                });
              } catch (updateError) {
                console.error('Error updating referral system document:', updateError);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching referred users from users collection:', error);
        }
      }
      
      // Set the partner data and referred users
      setReferralInfo(partnerData);
      setIsReferralPartner(true);
      setReferralCode(partnerData.code || '');
      
      // Set PayPal email if available
      if (partnerData.paypalEmail) {
        setPaypalEmail(partnerData.paypalEmail);
      }
      
      setReferredUsers(referredUsersData);
      
      // Calculate pending commissions safely
      let totalEarnedAmount = 0;
      try {
        referredUsersData.forEach(user => {
          if (user.subscription && user.subscription.active) {
            // Determine subscription date using fallbacks
            let subscriptionDate = null;
            if (user.subscription.startDate) {
              subscriptionDate = new Date(user.subscription.startDate);
            } else if (user.subscription.createdAt) {
              subscriptionDate = new Date(user.subscription.createdAt);
            } else if (user.activatedAt) {
              subscriptionDate = new Date(user.activatedAt);
            } else if (user.registeredAt) {
              subscriptionDate = new Date(user.registeredAt);
            } else {
              // If no date is available, use a date 60 days ago
              const fallbackDate = new Date();
              fallbackDate.setDate(fallbackDate.getDate() - 60);
              subscriptionDate = fallbackDate;
              console.log('Using fallback date for subscription:', fallbackDate);
            }
            
            // Calculate commission based on actual subscription plan price
            const planPrice = user.subscription.planPrice || 0;
            const commissionRate = partnerData.commission / 100;
            
            // Add base subscription commission
            totalEarnedAmount += planPrice * commissionRate;
            
            // Add renewal commissions if they exist
            if (user.renewals && Array.isArray(user.renewals)) {
              // Add commission for each renewal
              user.renewals.forEach(renewal => {
                const renewalPrice = renewal.planPrice || planPrice;
                totalEarnedAmount += renewalPrice * commissionRate;
              });
            }
          }
        });
      } catch (error) {
        console.error('Error calculating commissions:', error);
      }
      
      // Calculate total paid amount from payout history
      let totalPaidAmount = 0;
      try {
        const payoutsRef = collection(db, 'referralPayouts');
        const payoutsQuery = query(payoutsRef, where('partnerId', '==', user.uid), where('status', '==', 'completed'));
        const payoutsSnapshot = await getDocs(payoutsQuery);
        
        payoutsSnapshot.docs.forEach(doc => {
          const payout = doc.data();
          totalPaidAmount += parseFloat(payout.amount) || 0;
        });
      } catch (error) {
        console.error('Error calculating total paid amount:', error);
      }
      
      // Calculate pending amount by subtracting paid amount from total earned
      const pendingAmount = Math.max(0, totalEarnedAmount - totalPaidAmount);
      setPendingCommissions(pendingAmount);
      
      // Fetch payout history
      try {
        const payoutsRef = collection(db, 'referralPayouts');
        const payoutsQuery = query(payoutsRef, where('partnerId', '==', user.uid));
        const payoutsSnapshot = await getDocs(payoutsQuery);
        
        const payoutHistoryData = payoutsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort by date, newest first
        payoutHistoryData.sort((a, b) => {
          return new Date(b.date) - new Date(a.date);
        });
        
        setPayoutHistory(payoutHistoryData);
      } catch (error) {
        console.error('Error fetching payout history:', error);
        setPayoutHistory([]);
      }
    } catch (error) {
      console.error('Error fetching referral information:', error);
    } finally {
      setLoadingReferrals(false);
    }
  };

  // Copy referral link to clipboard
  const copyReferralLink = () => {
    const referralLink = `${window.location.origin}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(referralLink);
    setSuccess('Referral link copied to clipboard!');
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccess('');
    }, 3000);
  };

  // Sync referred users' subscription data
  const syncReferredUsersSubscriptions = async () => {
    try {
      setSyncingReferrals(true);
      console.log('Starting manual sync of referred users subscriptions...');
      
      // Get all users who were referred by this partner
      const usersRef = collection(db, 'users');
      const referredUsersQuery = query(usersRef, where('referrerId', '==', user.uid));
      const referredUsersSnapshot = await getDocs(referredUsersQuery);
      
      if (referredUsersSnapshot.empty) {
        console.log('No referred users found');
        alert('No referred users found to sync');
        setSyncingReferrals(false);
        return;
      }
      
      console.log(`Found ${referredUsersSnapshot.docs.length} referred users to sync`);
      
      // Get the referral system document
      const referralSystemRef = doc(db, 'referralSystem', user.uid);
      const referralSystemDoc = await getDoc(referralSystemRef);
      
      if (!referralSystemDoc.exists()) {
        console.log('No referral system document found, creating one');
        // Create the referral system document if it doesn't exist
        await setDoc(doc(db, 'referralSystem', user.uid), {
          partnerId: user.uid,
          partnerName: userData.fullName || '',
          partnerEmail: userData.email || '',
          code: userData.partnerReferralCode || referralCode,
          commission: userData.referralCommission || 10,
          isActive: true,
          referredUsers: [],
          referralStats: { total: 0, active: 0 },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
      
      // Process each referred user
      const referredUsersData = [];
      let activeCount = 0;
      
      for (const userDoc of referredUsersSnapshot.docs) {
        const userData = userDoc.data();
        const userId = userDoc.id;
        
        // Get the user's subscription data
        const subscription = userData.subscription || null;
        
        // Format subscription data
        const formattedSubscription = subscription ? {
          active: subscription.status === 'active',
          planType: subscription.type || null,
          planName: subscription.planName || null,
          planPrice: subscription.price || 0,
          startDate: subscription.startDate || null,
          cancelDate: subscription.cancelDate || null,
          status: subscription.status || 'inactive'
        } : {
          active: false,
          status: 'inactive'
        };
        
        // Add to referred users array
        referredUsersData.push({
          userId: userId,
          email: userData.email || '',
          fullName: userData.fullName || '',
          registeredAt: userData.createdAt || new Date().toISOString(),
          activatedAt: formattedSubscription.active ? userData.updatedAt || new Date().toISOString() : null,
          subscription: formattedSubscription
        });
        
        // Count active subscriptions
        if (formattedSubscription.active) {
          activeCount++;
        }
      }
      
      // Update the referral system document
      await updateDoc(referralSystemRef, {
        referredUsers: referredUsersData,
        referralStats: { 
          total: referredUsersData.length, 
          active: activeCount 
        },
        updatedAt: new Date().toISOString()
      });
      
      console.log('Successfully synced referred users subscriptions');
      alert('Successfully synced referred users data');
      
      // Refresh the referral information
      await fetchReferralInfo();
    } catch (error) {
      console.error('Error syncing referred users subscriptions:', error);
      alert('Error syncing referred users data: ' + error.message);
    } finally {
      setSyncingReferrals(false);
    }
  };

  const modelOptions = models.map(model => ({
    label: model.id,
    value: model.id
  }));

  return (
    <Page title="Settings">
      {loading && <Loading />}
      
      {error && (
        <Banner status="critical" onDismiss={() => setError('')}>
          <p>{error}</p>
        </Banner>
      )}
      
      {success && (
        <Banner status="success" onDismiss={() => setSuccess('')}>
          <p>{success}</p>
        </Banner>
      )}

      <Layout>
        <Layout.Section>
          <Tabs
            tabs={[
              {
                id: 'account',
                content: 'Account Settings',
                accessibilityLabel: 'Account Settings',
                panelID: 'account-settings-panel',
              },
              // Only show OpenAI settings for admin users
              ...(userData?.isAdmin ? [{
                id: 'openai',
                content: 'OpenAI Settings',
                accessibilityLabel: 'OpenAI Settings',
                panelID: 'openai-settings-panel',
              }] : []),
              {
                id: 'referral',
                content: 'Referral Program',
                accessibilityLabel: 'Referral Program',
                panelID: 'referral-program-panel',
              },
              {
                id: 'help',
                content: 'Help & Resources',
                accessibilityLabel: 'Help & Resources',
                panelID: 'help-resources-panel',
              },
            ]}
            selected={selectedTab}
            onSelect={(index) => setSelectedTab(index)}
          />
          
          {selectedTab === 0 && (
            <Card title="Account Settings" sectioned>
              <Card.Section title="User Profile">
                <Stack vertical spacing="loose">
                  <Stack distribution="equalSpacing" alignment="center">
                    <Stack vertical spacing="tight">
                      <TextContainer>
                        <Text variant="bodyMd" as="p">
                          <strong>Email:</strong> {user?.email}
                        </Text>
                        <Text variant="bodyMd" as="p">
                          <strong>Full Name:</strong> {userData?.fullName || 'Not set'}
                        </Text>
                      </TextContainer>
                    </Stack>
                    <Button onClick={() => setShowFullNameModal(true)}>
                      Update Full Name
                    </Button>
                  </Stack>
                </Stack>
              </Card.Section>
              
              <Card.Section title="Password Management">
                <Stack vertical spacing="loose">
                  <Stack distribution="equalSpacing" alignment="center">
                    <Stack vertical spacing="tight">
                      <TextContainer>
                        <Text variant="bodyMd" as="p">
                          Change your password or reset it if forgotten.
                        </Text>
                      </TextContainer>
                    </Stack>
                    <Stack>
                      <Button onClick={() => setShowPasswordModal(true)}>
                        Change Password
                      </Button>
                      <Button onClick={handlePasswordReset}>
                        Reset Password
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Card.Section>
              
              <Card.Section title="Account Actions">
                <Stack distribution="equalSpacing" alignment="center">
                  <Text variant="bodyMd" as="p">
                    Sign out of your account.
                  </Text>
                  <ButtonGroup>
                    <Button onClick={handleLogout} destructive>
                      Logout
                    </Button>
                    <Button onClick={() => window.location.href = 'mailto:support@ecomevolutionpro.com?subject=Support%20for%20Shopify%20Product%20Editor%20Pro'}>
                      Support
                    </Button>
                  </ButtonGroup>
                </Stack>
              </Card.Section>
            </Card>
          )}
          
          {selectedTab === 1 && userData?.isAdmin && (
            <Card title="OpenAI Settings" sectioned>
              <FormLayout>
                <Stack vertical>
                  <Text variant="bodyMd" as="p" color="subdued">
                    We recommend to use a mini model for cheap generating like gpt-4o-mini
                  </Text>
                  <TextField
                    label="OpenAI API Key"
                    value={openaiSettings.apiKey}
                    onChange={(value) => setOpenaiSettings({ ...openaiSettings, apiKey: value })}
                    type={showApiKey ? "text" : "password"}
                    autoComplete="off"
                    connectedRight={
                      <Button onClick={() => setShowApiKey(!showApiKey)}>
                        {showApiKey ? 'Hide' : 'Show'}
                      </Button>
                    }
                  />

                  {openaiSettings.model && (
                    <>
                      <TextField
                        label="Current Model"
                        value={openaiSettings.model}
                        readOnly
                        disabled
                      />
                      <TextField
                        label="Maximum Tokens"
                        value={openaiSettings.maxTokens?.toString() || '0'}
                        readOnly
                        disabled
                        helpText="Maximum tokens available for this model"
                      />
                    </>
                  )}
                  
                  <Button
                    primary
                    onClick={handleSaveApiKey}
                    loading={loading || loadingModels}
                    disabled={!openaiSettings.apiKey}
                  >
                    Save API Key & Load Models
                  </Button>

                  {models.length > 0 && (
                    <>
                      <Select
                        label="Select GPT Model"
                        options={modelOptions}
                        value={selectedModel}
                        onChange={setSelectedModel}
                        disabled={loading}
                      />
                      
                      <Button
                        primary
                        onClick={handleSaveModel}
                        loading={loading}
                        disabled={!selectedModel}
                      >
                        Save Model Selection
                      </Button>
                    </>
                  )}
                </Stack>
              </FormLayout>
            </Card>
          )}
          
          {(userData?.isAdmin ? selectedTab === 2 : selectedTab === 1) && (
            <Card title="Referral Program" sectioned>
              {isReferralPartner ? (
                <>
                  <Card.Section title="Your Referral Link">
                    <Stack vertical spacing="loose">
                      <div style={{ width: '100%' }}>
                        <TextField
                          label="Share this link with others"
                          value={`${window.location.origin}/register?ref=${referralCode}`}
                          readOnly
                          connectedRight={
                            <Button onClick={copyReferralLink} icon={DuplicateMinor}>
                              Copy
                            </Button>
                          }
                        />
                      </div>
                      <Banner status="info" fullWidth>
                        <p>When someone signs up using your referral link, you'll earn a commission on their subscription!</p>
                      </Banner>
                    </Stack>
                  </Card.Section>
                  
                  <Card.Section title="Referred Users">
                    {loadingReferrals ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Loading />
                      </div>
                    ) : (
                      <>
                        {referredUsers.length > 0 ? (
                          <div style={{ marginTop: '20px' }}>
                            <DataTable
                              columnContentTypes={['text', 'text', 'text', 'text']}
                              headings={['User', 'Joined Date', 'Subscription Status', 'Commission']}
                              rows={referredUsers.map(user => [
                                user.email,
                                user.createdAt ? (typeof user.createdAt === 'object' && user.createdAt.seconds ? 
                                  new Date(user.createdAt.seconds * 1000).toLocaleDateString() : 
                                  new Date(user.createdAt).toLocaleDateString()) : 'Unknown',
                                user.subscription && user.subscription.active ? (
                                  <Badge status="success">Active</Badge>
                                ) : (
                                  <Badge>Inactive</Badge>
                                ),
                                user.subscription && user.subscription.active ? 
                                  `$${((user.subscription.planPrice) * (referralInfo?.commission / 100)).toFixed(2)}` : 
                                  '$0.00'
                              ])}
                            />
                          </div>
                        ) : (
                          <div style={{ marginTop: '20px' }}>
                            <EmptyState
                              heading="No referrals yet"
                              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                            >
                              <p>Share your referral link to start earning commissions!</p>
                            </EmptyState>
                          </div>
                        )}
                      </>
                    )}
                  </Card.Section>
                  
                  <Card.Section title="Referral Statistics">
                    {loadingReferrals ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Loading />
                      </div>
                    ) : (
                      <>
                        <Stack distribution="equalSpacing" alignment="center" spacing="loose">
                          <Stack vertical spacing="tight">
                            <TextContainer>
                              <Text variant="headingMd" as="h3">{referredUsers.length}</Text>
                              <Text variant="bodyMd" as="p">Total Referred Users</Text>
                            </TextContainer>
                          </Stack>
                          <Stack vertical spacing="tight">
                            <TextContainer>
                              <Text variant="headingMd" as="h3">
                                {referredUsers.filter(user => user.subscription && user.subscription.active).length}
                              </Text>
                              <Text variant="bodyMd" as="p">Active Subscriptions</Text>
                            </TextContainer>
                          </Stack>
                          <Stack vertical spacing="tight">
                            <TextContainer>
                              <Text variant="headingMd" as="h3">{referralInfo?.commission || 0}%</Text>
                              <Text variant="bodyMd" as="p">Commission Rate</Text>
                            </TextContainer>
                          </Stack>
                        </Stack>
                      </>
                    )}
                  </Card.Section>
                  
                  <Card.Section title="Commission & Payouts">
                    <Stack vertical spacing="loose">
                      <Stack distribution="equalSpacing" alignment="center">
                        <Stack vertical spacing="tight">
                          <TextContainer>
                            <Text variant="headingMd" as="h3">${pendingCommissions.toFixed(2)}</Text>
                            <Text variant="bodyMd" as="p">Pending Commission</Text>
                          </TextContainer>
                        </Stack>
                        <Stack vertical spacing="tight">
                          <TextContainer>
                            <Text variant="headingMd" as="h3">
                              ${payoutHistory.length > 0 ? payoutHistory[0].amount.toFixed(2) : '0.00'}
                            </Text>
                            <Text variant="bodyMd" as="p">Last Payout</Text>
                          </TextContainer>
                        </Stack>
                        <Stack vertical spacing="tight">
                          <TextContainer>
                            <Text variant="headingMd" as="h3">
                              ${payoutHistory.reduce((total, payout) => total + payout.amount, 0).toFixed(2)}
                            </Text>
                            <Text variant="bodyMd" as="p">Total Earned</Text>
                          </TextContainer>
                        </Stack>
                      </Stack>
                      
                      <Banner status="info" fullWidth>
                        <p>Commissions are calculated based on active subscriptions from your referred users. Payouts are processed monthly.</p>
                      </Banner>
                      
                      {payoutHistory.length > 0 ? (
                        <>
                          <Text variant="headingMd" as="h3">Payout History</Text>
                          <DataTable
                            columnContentTypes={[
                              'text',
                              'numeric',
                              'text',
                              'text'
                            ]}
                            headings={[
                              'Date',
                              'Amount',
                              'Status',
                              'Transaction ID'
                            ]}
                            rows={payoutHistory.map(payout => [
                              new Date(payout.date).toLocaleDateString(),
                              `$${payout.amount.toFixed(2)}`,
                              <Badge status={payout.status === 'completed' ? 'success' : 'attention'}>
                                {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                              </Badge>,
                              payout.transactionId || '-'
                            ])}
                          />
                        </>
                      ) : (
                        <EmptyState
                          heading="No payout history yet"
                          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                        >
                          <p>Your payout history will appear here once you start earning commissions.</p>
                        </EmptyState>
                      )}
                    </Stack>
                  </Card.Section>
                  <Card.Section title="Payout Settings">
                    <Stack vertical spacing="loose">
                      <TextField
                        label="PayPal Email"
                        value={paypalEmail}
                        readOnly
                        helpText="Enter your PayPal email address to receive payouts"
                      />
                      <Button onClick={() => setShowPaypalModal(true)}>
                        Update PayPal Email
                      </Button>
                    </Stack>
                  </Card.Section>
                </>
              ) : (
                <Card.Section>
                  {referralCode && !isReferralPartner ? (
                    <Banner status="info">
                      <p>You were referred by a partner with code: <strong>{referralCode}</strong></p>
                    </Banner>
                  ) : null}
                  
                  <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <EmptyState
                      heading="Become a Referral Partner"
                      image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                      action={{
                        content: 'Apply to Join',
                        onAction: () => setShowReferralInfoModal(true)
                      }}
                    >
                      <p>Interested in becoming a referral partner? Learn more about our program and apply to join!</p>
                    </EmptyState>
                  </div>
                </Card.Section>
              )}
            </Card>
          )}
          {(userData?.isAdmin ? selectedTab === 3 : selectedTab === 2) && (
            <Card title="Help & Resources" sectioned>
              <Card.Section>
                <Stack vertical spacing="tight">
                  <Text variant="bodyMd" as="p">
                    Learn how to make the most of Shopify Product Editor Pro.
                  </Text>
                  <List>
                    <List.Item>
                      Create and manage AI-powered product templates
                    </List.Item>
                    <List.Item>
                      Bulk edit product titles and descriptions
                    </List.Item>
                    <List.Item>
                      Optimize your product content for better visibility
                    </List.Item>
                  </List>
                  <ButtonGroup>
                    <Button url="https://splendid-nape-e37.notion.site/Shopify-Product-Editor-Pro-19546db4a51980678977fe6dd71014f3" external>
                      Documentation
                    </Button>
                    <Button onClick={() => window.location.href = 'mailto:support@ecomevolutionpro.com?subject=Support%20for%20Shopify%20Product%20Editor%20Pro'}>
                      Support
                    </Button>
                  </ButtonGroup>
                </Stack>
              </Card.Section>
            </Card>
          )}
        </Layout.Section>
      </Layout>

      {/* Full Name Update Modal */}
      <Modal
        open={showFullNameModal}
        onClose={() => {
          setShowFullNameModal(false);
          setError('');
          // Reset to current value
          setNewFullName(userData?.fullName || '');
        }}
        title="Update Full Name"
        primaryAction={{
          content: 'Update',
          onAction: handleUpdateFullName,
          loading: loading,
          disabled: !newFullName.trim() || loading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowFullNameModal(false);
              setError('');
              // Reset to current value
              setNewFullName(userData?.fullName || '');
            }
          }
        ]}
      >
        <Modal.Section>
          {error && (
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          )}
          <FormLayout>
            <TextField
              label="Full Name"
              value={newFullName}
              onChange={setNewFullName}
              autoComplete="name"
              disabled={loading}
              required
              helpText="Enter your full name as you would like it to appear"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Modal
        open={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setError('');
          setCurrentPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }}
        title="Change Password"
        primaryAction={{
          content: 'Change Password',
          onAction: handleChangePassword,
          loading: loading,
          disabled: !currentPassword || !newPassword || !confirmPassword || loading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowPasswordModal(false);
              setError('');
              setCurrentPassword('');
              setNewPassword('');
              setConfirmPassword('');
            }
          }
        ]}
      >
        <Modal.Section>
          {error && (
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          )}
          <FormLayout>
            <TextField
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={setCurrentPassword}
              autoComplete="current-password"
              error={error && error.includes('current password') ? error : undefined}
              disabled={loading}
              required
            />
            <TextField
              type="password"
              label="New Password"
              value={newPassword}
              onChange={setNewPassword}
              autoComplete="new-password"
              error={error && error.includes('new password') ? error : undefined}
              helpText="Password must be at least 6 characters long"
              disabled={loading}
              required
            />
            <TextField
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              autoComplete="new-password"
              error={newPassword !== confirmPassword ? 'Passwords do not match' : undefined}
              disabled={loading}
              required
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Modal
        open={showPaypalModal}
        onClose={() => {
          setShowPaypalModal(false);
          setError('');
          setPaypalEmail(referralInfo?.paypalEmail || '');
        }}
        title="Update PayPal Email"
        primaryAction={{
          content: 'Update',
          onAction: handleUpdatePaypalEmail,
          loading: loading,
          disabled: !paypalEmail || !paypalEmail.includes('@') || loading
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowPaypalModal(false);
              setError('');
              setPaypalEmail(referralInfo?.paypalEmail || '');
            }
          }
        ]}
      >
        <Modal.Section>
          {error && (
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          )}
          <FormLayout>
            <TextField
              label="PayPal Email"
              value={paypalEmail}
              onChange={setPaypalEmail}
              autoComplete="email"
              disabled={loading}
              required
              helpText="Enter your PayPal email address to receive payouts"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      <Modal
        open={showReferralInfoModal}
        onClose={() => {
          setShowReferralInfoModal(false);
          setError('');
        }}
        title="🤝 Join Our Referral Program"
        primaryAction={{
          content: 'Apply Now',
          onAction: () => window.location.href = 'mailto:referral@elevia-solutions.com?subject=Referral%20Partner%20Application',
        }}
        secondaryActions={[]}
      >
        <Modal.Section>
          {error && (
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          )}
          <div style={{ padding: '10px 0' }}>
            <TextContainer>
              <Text variant="bodyLg" as="p">
                Earn money by sharing what you love.
              </Text>
              <Text variant="bodyMd" as="p" color="subdued">
                Do you have an engaged audience and know how to turn reach into results? Then our referral program is for you!
              </Text>
              
              <Text variant="headingMd" as="h3" fontWeight="bold">
                📌 What You Need to Know:
              </Text>
              
              <Text variant="headingSm" as="h4" fontWeight="medium">
                Who we're looking for:
              </Text>
              <Text variant="bodyMd" as="p">
                Creators, influencers, or networkers with a solid follower base and the ability to attract real customers.
              </Text>
              
              <Text variant="headingSm" as="h4" fontWeight="medium">
                Payout model:
              </Text>
              <Text variant="bodyMd" as="p">
                You earn a commission for every successful subscription you refer.
                Payouts are made 30 days after a user subscribes — this delay allows time for refunds or cancellations.
              </Text>
              
              <Text variant="headingSm" as="h4" fontWeight="medium">
                Referral dashboard:
              </Text>
              <Text variant="bodyMd" as="p">
                Once approved, you'll get access to a dashboard right on this page where you can:
              </Text>
              <List type="bullet">
                <List.Item>See who you've referred</List.Item>
                <List.Item>Track active subscriptions</List.Item>
                <List.Item>Monitor open and completed payouts</List.Item>
              </List>
              
              <Text variant="headingMd" as="h3" fontWeight="bold" color="success">
                🚀 Ready to Get Started?
              </Text>
              <Text variant="bodyMd" as="p">
                Tap the button below to apply – this will open your email program with a pre-filled message.
              </Text>
            </TextContainer>
          </div>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
